import React, { useState } from 'react';

interface TagInputProps {
	tags: string[];
	onAddTag: (tag: string) => void;
	onRemoveTag: (index: number) => void;
	placeholder: string;
	validate: (value: string, existingTags: string[]) => string | null;
	label: string;
	error?: string;
}

function TagInput({ tags, onAddTag, onRemoveTag, placeholder, validate, label, error }: TagInputProps) {
	const [inputValue, setInputValue] = useState('');
	const [inputError, setInputError] = useState('');

	const handleAdd = () => {
		const trimmed = inputValue.trim().toLowerCase();
		if (!trimmed) return;

		const validationError = validate(trimmed, tags);
		if (validationError) {
			setInputError(validationError);
			return;
		}

		onAddTag(trimmed);
		setInputValue('');
		setInputError('');
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			handleAdd();
		}
		if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
			onRemoveTag(tags.length - 1);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
		if (inputError) setInputError('');
	};

	const displayError = inputError || error;

	return (
		<div>
			<p className='text-primaryViolet font-semibold text-lg text-left'>{label}</p>
			<div
				className={`border-b p-2 mt-2 flex flex-row flex-wrap items-center gap-2 ${
					displayError ? 'border-b-redError' : 'border-b-deselectPurple'
				}`}
			>
				{tags.map((tag, index) => (
					<div key={`${tag}-${index}`} className='flex items-center gap-1 rounded-full bg-[#F8F8FB] border border-deselectPurple px-3 py-1'>
						<span className='text-[14px] text-primaryViolet'>{tag}</span>
						<button
							type='button'
							className='text-mudPurple hover:text-redError text-[14px] font-bold ml-1'
							onClick={() => onRemoveTag(index)}
						>
							×
						</button>
					</div>
				))}
				<input
					type='text'
					value={inputValue}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onBlur={() => {
						if (inputValue.trim()) handleAdd();
					}}
					placeholder={tags.length === 0 ? placeholder : ''}
					className='flex-1 min-w-[150px] text-primaryViolet outline-none bg-transparent text-[14px]'
					autoComplete='off'
				/>
			</div>
			{displayError && <p className='text-redError text-[12px] mt-1'>{displayError}</p>}
		</div>
	);
}

export default TagInput;
